/* eslint-disable */
import React, { useRef, useEffect } from 'react'
import * as THREE from 'three'
import { useFrame, useThree, useUpdate, applyProps } from 'react-three-fiber'

import getVectorsFromPolynomial from './Helpers/Trajectory'

/* Mock Trajectory Data
  const data = [
    {
      polynomial_x: [
        27.41442803575749,
        -70.18051616123861,
        17.08407598318996,
        -4.611170856221415,
        0.9642700567627676,
        -0.15678243470324288,
        0.018482780660988046,
        -0.0013244573787721601,
        0.00004142852081362038,
      ],
      polynomial_y: [
        -55.20183821755383,
        151.1982528759672,
        -54.223969969570376,
        16.10990600476229,
        -3.360033559385342,
        0.4964499549831538,
        -0.050335790278066436,
        0.0031593983350885683,
        -0.00009087582861788655,
      ],
      polynomial_z: [
        -41.919334657074806,
        110.12632683441825,
        -10.397856679817393,
        -5.301545560460903,
        2.310218779696486,
        -0.5422136932317498,
        0.07238640761767902,
        -0.005065950458812853,
        0.0001449962234188665,
      ],
    },
  ];
*/

function BallPath({
    polynomial,
    position,
    color,
    type = 'line',
    delay = 0,
    showSphere = false,
    isAnimated = false,
    isFinished,
    lastUpdate,
}) {
    const { camera } = useThree()

    const mesh = useRef()
    const sphere = useRef()

    let end = 0
    let max = null
    let step = null
    let endVector

    const updateLineBuffer = useUpdate((geometry) => {
        geometry.setFromPoints(vectors)
        geometry.attributes.position.needsUpdate = true
        if (isAnimated) {
            geometry.setDrawRange(0, 0)
        }
        max = geometry.attributes.position.count
        end = 0
        step = geometry.attributes.position.count / 100
        endVector = vectors[vectors.length - 1]
    }, [])

    const updateTubeBuffer = useUpdate((geometry) => {
        const catmull = new THREE.CatmullRomCurve3(vectors, false)

        const tube = new THREE.TubeGeometry(catmull, 100, 1, 8, false)
        geometry.fromGeometry(tube)

        if (isAnimated) {
            geometry.setDrawRange(0, 0)
        }
        max = geometry.attributes.position.count
        end = 0
        step = geometry.attributes.position.count / 100
        endVector = vectors[vectors.length - 1]
    }, [])

    const vectors = getVectorsFromPolynomial(polynomial)

    useFrame((state) => animateTrajectory(state))

    let isStart = false

    if (isAnimated) {
        setTimeout(() => {
            isStart = true
        }, delay)
    }

    function animateTrajectory(state) {
        if (mesh && mesh.current && isStart) {
            const m = mesh.current

            if (m && camera.position.x === position[0]) {
                const nMax = max
                const shootOnce = true
                if (shootOnce && end + step >= nMax) {
                    m.geometry.setDrawRange(max, max)
                } else {
                    end = (end + step) % max
                    const half = end * 0.95
                    m.geometry.setDrawRange(half, end)

                    if (showSphere) {
                        const scale = 6
                        sphere.current.material.opacity = 1

                        const pos = m.geometry.getAttribute('position')
                        const currentLocation = (half + end).toFixed(0) * 3
                        if (pos.array && pos.array[currentLocation]) {
                            sphere.current.position.x =
                                pos.array[currentLocation]
                            sphere.current.position.y =
                                pos.array[currentLocation + 1]
                            sphere.current.position.z =
                                pos.array[currentLocation + 2]
                        } else {
                            sphere.current.position.x = endVector.x
                            sphere.current.position.y = endVector.y
                            sphere.current.position.z = endVector.z
                        }
                    }
                }
            }
        }
    }

    function triggerShooting() {
        end = 0
    }

    let trajectory
    if (type === 'catmull') {
        trajectory = (
            <mesh ref={mesh} position={position}>
                <meshPhongMaterial
                    attach="material"
                    color={color || 0xff0000}
                    wireframe={false}
                    opacity={1}
                    transparent
                    shininess={10}
                    side={THREE.DoubleSide}
                    depthWrite
                />
                <bufferGeometry ref={updateTubeBuffer} attach="geometry" />
            </mesh>
        )
    } else {
        trajectory = (
            <line
                ref={mesh}
                position={position}
                receiveShadow
                castShadow={false}
            >
                <bufferGeometry ref={updateLineBuffer} attach="geometry" />
                <lineBasicMaterial
                    attach="material"
                    color={color || 0xff0000}
                    transparent
                    opacity={1}
                />
            </line>
        )
    }

    let ball = null
    if (showSphere) {
        ball = (
            <mesh
                onClick={(e) => {
                    triggerShooting()
                }}
                ref={sphere}
                visible
                position={vectors[0]}
                receiveShadow
                castShadow
            >
                <sphereGeometry attach="geometry" args={[4, 16, 16]} />
                <meshPhongMaterial
                    attach="material"
                    color={color || 0xff0000}
                    transparent
                    opacity={0}
                />
            </mesh>
        )
    }

    return (
        <group
            onUpdate={() => {
                isFinished(lastUpdate)
            }}
        >
            ${trajectory}${ball}
        </group>
    )
}

export default BallPath
